import { isSwiftCodeValid, validateIban } from '@app/domain/accountDetailsValidators';
import { removeIrrelevantAccountDetailsFields } from '@app/domain/accountsDetailsHelpers';
import {
  getAccountValidationFields,
  getAccountValidationForLocalFormatFields,
  VERIFICATION_ACCOUNT_TYPE,
} from '@app/domain/accountVerification';
import { COUNTRY_CODES } from '@app/domain/countries';
import COUNTRIES from '@app/domain/countries';
import {
  PayeeMetricsResults,
  SearchStatusType,
  SergeeMetricResult,
  SergeeSearchResult,
  SergeeSearchScore,
  UIMetricResponse,
} from '@mortee/domain/morteeAccountVerification';
import { UnifiedPayee } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';
import { searchPayeeAccount } from '@mortee/services/searchService';
import { z } from 'zod';

import { clabe } from 'clabe-validator';

export const validateIbanCode = async (value: string): Promise<boolean> => {
  if (!value) {
    return true; // Empty values are handled by required validation
  }

  try {
    await validateIban(null, value);
    return true;
  } catch {
    return false;
  }
};

export const accountVerificationSchema = z
  .object({
    payeeName: z.string().nonempty('Payee name is required'),
    taxId: z.string().optional(),
    accountType: z.enum([VERIFICATION_ACCOUNT_TYPE.domestic, VERIFICATION_ACCOUNT_TYPE.iban, VERIFICATION_ACCOUNT_TYPE.swift]),
    country: z.string().optional(),
    bankCode: z.string().optional(),
    branchCode: z.string().optional(),
    accountNumber: z.string().optional(),
    iban: z.string().optional(),
    swiftBic: z.string().optional(),
    knoxOrTaxId: z.string().optional(),
    mexicoAccountFormat: z.enum(['domestic', 'clabe']).optional(),
    localFormat: z.string().optional(),
  })
  .superRefine(async (data, ctx) => {
    if (data.accountType === VERIFICATION_ACCOUNT_TYPE.domestic) {
      if (!data.country) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Country is required for domestic accounts', path: ['country'] });
      } else {
        const countryCode = getCountryCodeByName(data.country);
        if (countryCode) {
          // Handle Mexico with CLABE option
          if (countryCode === COUNTRY_CODES.Mexico) {
            if (!data.mexicoAccountFormat) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please select account format',
                path: ['mexicoAccountFormat'],
              });
            } else if (data.mexicoAccountFormat === 'clabe') {
              // Validate CLABE format
              const localFormatFields = getAccountValidationForLocalFormatFields(countryCode);
              if (localFormatFields?.localFormat) {
                if (!data.localFormat) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${localFormatFields.localFormat.displayName} is required`,
                    path: ['localFormat'],
                  });
                } else {
                  const rules = localFormatFields.localFormat.decorator(true).rules;
                  if (rules) {
                    for (const rule of rules) {
                      if (rule.len && data.localFormat && data.localFormat.length !== rule.len) {
                        ctx.addIssue({
                          code: z.ZodIssueCode.custom,
                          message: rule.translatedMessage?.['key']?.[1] || `Must be exactly ${rule.len} digits`,
                          path: ['localFormat'],
                        });
                      }
                      if (rule.pattern && data.localFormat && !rule.pattern.test(data.localFormat)) {
                        ctx.addIssue({
                          code: z.ZodIssueCode.custom,
                          message: rule.translatedMessage?.[1] || 'Invalid format',
                          path: ['localFormat'],
                        });
                      }
                    }
                  }
                  const clabeValidation = clabe.validate(data.localFormat || '');
                  if (!clabeValidation.ok) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Invalid CLABE format',
                      path: ['localFormat'],
                    });
                  }
                }
              }
            } else {
              // Validate regular domestic fields for Mexico
              const domesticFields = getAccountValidationFields(countryCode);
              if (domesticFields) {
                if (domesticFields.bankCode) {
                  if (!data.bankCode) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: `${domesticFields.bankCode.displayName} is required`,
                      path: ['bankCode'],
                    });
                  }

                  const rules = domesticFields.bankCode.decorator(true).rules;
                  if (rules) {
                    for (const rule of rules) {
                      if (rule.pattern && data.bankCode && !rule.pattern.test(data.bankCode)) {
                        ctx.addIssue({
                          code: z.ZodIssueCode.custom,
                          message: rule.translatedMessage?.['key']?.[1],
                          path: ['bankCode'],
                        });
                      }
                    }
                  }
                }
                if (domesticFields.branchCode && !data.branchCode) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${domesticFields.branchCode.displayName} is required`,
                    path: ['branchCode'],
                  });
                }

                const branchCodeRules = domesticFields.branchCode?.decorator(true).rules;
                if (branchCodeRules) {
                  for (const rule of branchCodeRules) {
                    if (rule.pattern && data.branchCode && !rule.pattern.test(data.branchCode)) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: rule.translatedMessage?.['key']?.[1],
                        path: ['branchCode'],
                      });
                    }
                  }
                }

                if (domesticFields.accountNumber && !data.accountNumber) {
                  const accountNumberDisplayName = domesticFields.accountNumber.displayName || 'Account number';
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${accountNumberDisplayName} is required`,
                    path: ['accountNumber'],
                  });
                }
                const accountNumberRules = domesticFields.accountNumber?.decorator(true).rules;
                if (accountNumberRules) {
                  for (const rule of accountNumberRules) {
                    if (rule.pattern && data.accountNumber && !rule.pattern.test(data.accountNumber)) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: rule.translatedMessage?.['key']?.[1],
                        path: ['accountNumber'],
                      });
                    }
                  }
                }
              }
            }
          } else {
            // Handle other countries (non-Mexico)
            const domesticFields = getAccountValidationFields(countryCode);
            if (domesticFields) {
              if (domesticFields.bankCode) {
                if (!data.bankCode) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${domesticFields.bankCode.displayName} is required`,
                    path: ['bankCode'],
                  });
                }

                const rules = domesticFields.bankCode.decorator(true).rules;
                if (rules) {
                  for (const rule of rules) {
                    if (rule.pattern && data.bankCode && !rule.pattern.test(data.bankCode)) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: rule.translatedMessage?.['key']?.[1],
                        path: ['bankCode'],
                      });
                    }
                  }
                }
              }
              if (domesticFields.branchCode && !data.branchCode) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: `${domesticFields.branchCode.displayName} is required`,
                  path: ['branchCode'],
                });
              }

              const branchCodeRules = domesticFields.branchCode?.decorator(true).rules;
              if (branchCodeRules) {
                for (const rule of branchCodeRules) {
                  if (rule.pattern && data.branchCode && !rule.pattern.test(data.branchCode)) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: rule.translatedMessage?.['key']?.[1],
                      path: ['branchCode'],
                    });
                  }
                }
              }

              if (domesticFields.accountNumber && !data.accountNumber) {
                const accountNumberDisplayName = domesticFields.accountNumber.displayName || 'Account number';
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: `${accountNumberDisplayName} is required`,
                  path: ['accountNumber'],
                });
              }
              const accountNumberRules = domesticFields.accountNumber?.decorator(true).rules;
              if (accountNumberRules) {
                for (const rule of accountNumberRules) {
                  if (rule.pattern && data.accountNumber && !rule.pattern.test(data.accountNumber)) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: rule.translatedMessage?.['key']?.[1],
                      path: ['accountNumber'],
                    });
                  }
                }
              }
            }
          }
        } else {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid country selected', path: ['country'] });
        }
      }
    } else if (data.accountType === VERIFICATION_ACCOUNT_TYPE.iban) {
      if (!data.iban) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'IBAN is required', path: ['iban'] });
      } else if (!(await validateIbanCode(data.iban))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid IBAN', path: ['iban'] });
      }
    } else if (data.accountType === VERIFICATION_ACCOUNT_TYPE.swift) {
      if (!data.swiftBic) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SWIFT/BIC is required', path: ['swiftBic'] });
      } else if (!isSwiftCodeValid(data.swiftBic)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid SWIFT/BIC code', path: ['swiftBic'] });
      }
      if (!data.accountNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Account number is required for SWIFT/BIC',
          path: ['accountNumber'],
        });
      }
    }
  });

export type AccountVerificationForm = z.infer<typeof accountVerificationSchema>;

const getCountryCodeByName = (name: string): string | undefined => {
  if (!name) return undefined;
  const upperCaseName = name.toUpperCase();
  for (const code in COUNTRIES) {
    if (COUNTRIES[code as COUNTRY_CODES]?.name.toUpperCase() === upperCaseName) {
      return code;
    }
  }
  return undefined;
};

interface HandleSubmitArgs {
  currentFormData: AccountVerificationForm;
  selectedPayee: UnifiedPayee | null;
  countryCode: string;
  setVerificationError: (error: Error | null) => void;
  setShowResult: (show: boolean) => void;
  setIsVerifying: (isVerifying: boolean) => void;
  setVerificationResult: (result: SergeeSearchResult | null) => void;
  setFormDataPersistent: (data: AccountVerificationForm) => void;
  setMissingFields: (fields: string[]) => void;
  setShowMissingFields: (show: boolean) => void;
}

export const handleAccountVerificationSubmit = async (args: HandleSubmitArgs): Promise<void> => {
  const {
    currentFormData,
    selectedPayee,
    countryCode,
    setVerificationError,
    setShowResult,
    setIsVerifying,
    setVerificationResult,
    setFormDataPersistent,
    setMissingFields,
    setShowMissingFields,
  } = args;

  setIsVerifying(true);
  setVerificationResult(null);
  setVerificationError(null);
  setFormDataPersistent(currentFormData);

  let accountDetails: Partial<MorteeAccountDetailsExtended> = {
    accountNumber: currentFormData.accountNumber || undefined,
    bankCode: currentFormData.bankCode || undefined,
    branchCode: currentFormData.branchCode || undefined,
    iban: currentFormData.iban || undefined,
    swiftCode: currentFormData.swiftBic || undefined,
    countryCode: countryCode,
    localFormat: currentFormData.localFormat || undefined,
  };

  accountDetails = removeIrrelevantAccountDetailsFields(accountDetails);
  const payee = {
    id: currentFormData.taxId ? undefined : selectedPayee?.id,
    ids: selectedPayee?.ids,
    name: selectedPayee?.names?.[0]?.name || currentFormData.payeeName,
    taxId: currentFormData.taxId || undefined,
  };

  try {
    const result = await searchPayeeAccount(payee, accountDetails);
    if (result?.status === SearchStatusType.QuickCheckMissingDomesticAccount) {
      setMissingFields([payee?.taxId ? '' : 'taxId', 'domesticFields']);
      setFormDataPersistent({ ...currentFormData, iban: '', swiftBic: '' });
      setShowMissingFields(true);
    } else if (result?.status === SearchStatusType.QuickCheckMissingIban) {
      setMissingFields(['iban']);
      setFormDataPersistent({ ...currentFormData, iban: '', swiftBic: '', bankCode: '', branchCode: '', accountNumber: '' });
      setShowMissingFields(true);
    } else if (result?.status === SearchStatusType.QuickCheckMissingTaxId) {
      setMissingFields(['taxId']);
      setFormDataPersistent({ ...currentFormData, taxId: '' });
      setShowMissingFields(true);
    } else if (result?.status === SearchStatusType.QuickCheckMissingLocalFormat) {
      setMissingFields(['localFormat']);
      setFormDataPersistent({
        ...currentFormData,
        localFormat: '',
        bankCode: '',
        branchCode: '',
        accountNumber: '',
        swiftBic: '',
      });
      setShowMissingFields(true);
    } else if (result?.status === SearchStatusType.QuickCheckMissingSwiftCode) {
      setMissingFields(['swiftBic']);
      setFormDataPersistent({ ...currentFormData, swiftBic: '', bankCode: '', branchCode: '', iban: '', localFormat: '' });
      setShowMissingFields(true);
    } else {
      setMissingFields([]);
      setShowMissingFields(false);
      setShowResult(true);
      setVerificationResult(result);
    }
    setVerificationResult(result);
  } catch (error: any) {
    setVerificationError(error instanceof Error ? error : new Error('Verification failed'));
    setShowResult(false);
    setMissingFields([]);
    setShowMissingFields(false);
    setVerificationResult(null);
  }
  setIsVerifying(false);
};

export const formatOverallStatus = (score: SergeeSearchScore): string => {
  switch (score) {
    case SergeeSearchScore.AAA:
      return 'AAA';
    case SergeeSearchScore.AA:
      return 'AA';
    case SergeeSearchScore.A:
      return 'A';
    case SergeeSearchScore.Unknown:
      return '?';
    case SergeeSearchScore.NotValidated:
      return 'Not found';
    case SergeeSearchScore.NotRelevant:
      return 'Not relevant';
    default:
      return score;
  }
};

export const formatSergeeMetricResult = (result: SergeeMetricResult): string => {
  switch (result) {
    case SergeeMetricResult.KnoxValidationRequired:
      return 'Knox Verify Required';
    case SergeeMetricResult.NotRelevant:
      return 'Not relevant';
    case SergeeMetricResult.PartialMatch:
      return 'Match';
    default:
      return result;
  }
};

export const formatMetricsLabel = (label: keyof PayeeMetricsResults): string => {
  switch (label) {
    case 'accountNumber':
      return 'Account number';
    case 'bankCode':
      return 'Bank code';
    case 'branchCode':
      return 'Branch code';
    case 'countryCode':
      return 'Country code';
    case 'duns':
      return 'DUNS #';
    case 'iban':
      return 'IBAN';
    case 'swiftCode':
      return 'SWIFT/BIC';
    case 'name':
      return 'Name';
    case 'address':
      return 'Address';
    case 'taxId':
      return 'Tax ID';
    case 'additionalIds':
      return 'Additional IDs';
    case 'additionalNames':
      return 'Also known as';
    case 'payeeCountry':
      return 'Country';
    case 'accountCountry':
      return 'Account country';
    case 'bankName':
      return 'Bank name';
    default:
      return label;
  }
};

export const formatVerificationResult = (status: SearchStatusType): string => {
  switch (status) {
    case SearchStatusType.KnoxValidated:
      return 'Knox Verify';
    case SearchStatusType.QuickCheckValidated:
      return 'Quick Check';
    case SearchStatusType.QuickCheckMatch:
      return 'Quick Check';
    case SearchStatusType.QuickCheckNoPermission:
      return 'Not Found';
    case SearchStatusType.QuickCheckUnsupportedCountry:
      return 'Not Found';
    case SearchStatusType.QuickCheckMissingTaxId:
      return 'Not Found';
    case SearchStatusType.QuickCheckMissingDomesticAccount:
      return 'Not Found';
    case SearchStatusType.QuickCheckMissingIban:
      return 'Not Found';
    case SearchStatusType.QuickCheckFailedRestCall:
      return 'Not Found';
    case SearchStatusType.QuickCheckNoMatch:
      return 'Mismatch';
    case SearchStatusType.QuickCheckUnknown:
      return 'Not Found';
    case SearchStatusType.QuickCheckMissingSwiftCode:
      return 'Not Found';
    case SearchStatusType.QuickCheckMissingLocalFormat:
      return 'Not Found';
    case SearchStatusType.Error:
      return 'Error';
    case SearchStatusType.SelfValidated:
      return 'Self Validated';
    default:
      return 'Not Found';
  }
};

export const getScoreColor = (score: SergeeSearchScore): string => {
  if (score === SergeeSearchScore.AAA || score === SergeeSearchScore.AA || score === SergeeSearchScore.A) {
    return 'text-green-600';
  }
  if (
    score === SergeeSearchScore.NotValidated ||
    score === SergeeSearchScore.NotRelevant ||
    score === SergeeSearchScore.Unknown
  ) {
    return 'text-gray-400';
  }

  return 'text-red-600';
};

export const getIsNotValidatedMetric = (status: SergeeMetricResult): boolean =>
  [
    SergeeMetricResult.NotRelevant,
    SergeeMetricResult.Mismatch,
    SergeeMetricResult.Unknown,
    SergeeMetricResult.KnoxValidationRequired,
  ].includes(status);

export const getMetricsOrder = (): string[] => [
  'name',
  'additionalNames',
  'payeeCountry',
  'payeeAddress',
  'countryCode',
  'taxId',
  'additionalTaxIds',
  'additionalIds',
  'duns',
  'address',
  'additionalAddresses',
  'bankName',
  'accountCountry',
  'accountNumber',
  'bankCode',
  'branchCode',
  'iban',
  'swiftCode',
  'CLABE',
];

export const INITIATE_KNOX_VERIFY = 'Initiate Knox verify';
export const ACCEPTED = 'Accepted';
export const ACCEPT = 'Accept';
export const LOADING = 'loading';

export const sortVerificationDetails = (
  details: [string, UIMetricResponse][],
  metricsOrder: string[],
): [string, UIMetricResponse][] => {
  return details.sort(([aKey, aVal], [bKey, bVal]) => {
    const aIsNotRelevant = aVal.result === SergeeMetricResult.NotRelevant;
    const bIsNotRelevant = bVal.result === SergeeMetricResult.NotRelevant;

    if (aIsNotRelevant && !bIsNotRelevant) return 1;
    if (!aIsNotRelevant && bIsNotRelevant) return -1;

    // Both are NotRelevant or both are not, so use metricsOrder
    return metricsOrder.indexOf(aKey) - metricsOrder.indexOf(bKey);
  });
};

export const formatDate = (timestamp?: string | number): string => {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp));
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const shouldShowKnoxValidationButton = (status?: SearchStatusType): boolean => {
  return status === SearchStatusType.QuickCheckMatch;
};

export const shouldShowAcceptButton = (status?: SearchStatusType): boolean => {
  return status === SearchStatusType.QuickCheckMatch || status === SearchStatusType.QuickCheckValidated;
};

export const getAcceptButtonText = (isApproved: boolean, isApproving: boolean, status?: SearchStatusType): string => {
  if (isApproved || status === SearchStatusType.QuickCheckValidated) {
    return ACCEPTED;
  }
  if (isApproving) {
    return LOADING;
  }
  if (status === SearchStatusType.QuickCheckMatch) {
    return ACCEPT;
  }
  return INITIATE_KNOX_VERIFY;
};

export const isAcceptButtonDisabled = (isApproving: boolean, isApproved: boolean, status?: SearchStatusType): boolean => {
  return (
    isApproving ||
    isApproved ||
    (status !== undefined &&
      [SearchStatusType.QuickCheckValidated, SearchStatusType.QuickCheckUnknown, SearchStatusType.QuickCheckNoMatch].includes(
        status,
      ))
  );
};
